import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, Polyline, Popup, TileLayer } from 'react-leaflet';
import mapboxgl from 'mapbox-gl';
import { ensureLeafletDefaultIcon } from './LeafletDefaults';

/**
 * Basemap switch (default: MeMaps) with optional map.ir.
 *
 * Important: MeMaps raster tiles DO NOT work with Mapbox GL in-browser due to CORS,
 * so MeMaps is rendered with Leaflet.
 *
 * map.ir style URL: https://map.ir/vector/styles/main/mapir-xyz-style.json
 */
export default function MapIrMap({
  className,
  centerLatLng,
  zoom = 5,
  lineLatLngs = [],
  lineColor = '#0f766e',
  lineWidth = 4,
  markers = [],
  // Per your request, keep UI hidden for now.
  showProviderSwitch = false,
  defaultProvider = 'memaps', // 'memaps' | 'mapir'
}) {
  const apiKey = process.env.REACT_APP_MAP_IR_API_KEY;
  const [provider, setProvider] = useState(() => (showProviderSwitch ? defaultProvider : 'memaps'));

  useEffect(() => {
    if (!showProviderSwitch && provider !== 'memaps') setProvider('memaps');
  }, [showProviderSwitch, provider]);

  const effectiveProvider = provider === 'mapir' && !apiKey ? 'memaps' : provider;

  const map =
    effectiveProvider === 'mapir' ? (
      <MapIrMapbox
        apiKey={apiKey}
        centerLatLng={centerLatLng}
        zoom={zoom}
        lineLatLngs={lineLatLngs}
        lineColor={lineColor}
        lineWidth={lineWidth}
        markers={markers}
      />
    ) : (
      <MemapsLeafletMap
        centerLatLng={centerLatLng}
        zoom={zoom}
        lineLatLngs={lineLatLngs}
        lineColor={lineColor}
        lineWidth={lineWidth}
        markers={markers}
      />
    );

  return (
    <div className={`relative ${className || ''}`}>
      {map}

      {showProviderSwitch ? (
        <div className="absolute top-3 right-3 z-10">
          <div
            dir="rtl"
            className="inline-flex items-center rounded-2xl border border-gray-200 bg-white/95 backdrop-blur px-1 py-1 shadow-sm"
          >
            <button
              type="button"
              onClick={() => setProvider('memaps')}
              className={`px-3 py-2 rounded-2xl text-[11px] font-extrabold transition ${
                provider === 'memaps' ? 'bg-emerald-600 text-white shadow' : 'text-gray-700 hover:bg-gray-50'
              }`}
              title="نمایش نقشه با MeMaps (رایگان)"
            >
              MeMaps
            </button>
            <button
              type="button"
              onClick={() => setProvider('mapir')}
              className={`px-3 py-2 rounded-2xl text-[11px] font-extrabold transition ${
                provider === 'mapir' ? 'bg-emerald-600 text-white shadow' : 'text-gray-700 hover:bg-gray-50'
              }`}
              title="نمایش نقشه با map.ir (نیازمند API Key)"
            >
              map.ir
            </button>
          </div>
        </div>
      ) : null}

      {showProviderSwitch && provider === 'mapir' && !apiKey ? (
        <div className="absolute inset-x-0 bottom-3 px-3 z-10">
          <div
            dir="rtl"
            className="rounded-2xl border border-amber-200 bg-amber-50/95 backdrop-blur p-3 text-right text-[12px] font-bold text-amber-900"
          >
            برای نمایش نقشهٔ <span className="font-black">map.ir</span> مقدار <code>REACT_APP_MAP_IR_API_KEY</code> را در فایل{' '}
            <code>.env</code> تنظیم کنید و سرور را ری‌استارت کنید. (فعلاً روی MeMaps می‌ماند)
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MemapsLeafletMap({ centerLatLng, zoom, lineLatLngs, lineColor, lineWidth, markers }) {
  useEffect(() => {
    ensureLeafletDefaultIcon();
  }, []);

  return (
    <MapContainer
      center={Array.isArray(centerLatLng) ? centerLatLng : [35.729054, 51.42047]}
      zoom={zoom}
      className="h-full w-full"
      scrollWheelZoom
    >
      <TileLayer
        attribution='&copy; <a href="https://memaps.ir" target="_blank" rel="noreferrer">MeMaps</a> | &copy; OpenStreetMap contributors'
        url="https://memaps.ir/hot/{z}/{x}/{y}.png"
        maxZoom={20}
        detectRetina
      />
      {Array.isArray(lineLatLngs) && lineLatLngs.length ? (
        <Polyline positions={lineLatLngs} pathOptions={{ color: lineColor, weight: lineWidth }} />
      ) : null}
      {(markers || []).map((m) => (
        <Marker key={m.id || `${m.lat},${m.lng}`} position={[m.lat, m.lng]}>
          {m.title || m.subtitle ? (
            <Popup>
              <div dir="rtl" className="text-right">
                {m.title ? <div className="font-bold">{m.title}</div> : null}
                {m.subtitle ? <div className="text-[11px] text-gray-600 mt-1">{m.subtitle}</div> : null}
              </div>
            </Popup>
          ) : null}
        </Marker>
      ))}
    </MapContainer>
  );
}

function MapIrMapbox({ apiKey, centerLatLng, zoom, lineLatLngs, lineColor, lineWidth, markers }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRefs = useRef([]);
  const pendingStyleRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  const centerLngLat = useMemo(() => {
    const lat = Array.isArray(centerLatLng) ? Number(centerLatLng[0]) : 35.729054;
    const lng = Array.isArray(centerLatLng) ? Number(centerLatLng[1]) : 51.42047;
    return [lng, lat];
  }, [centerLatLng]);

  useEffect(() => {
    if (!containerRef.current) return;
    if (!apiKey) return;

    // One-time handshake used by Map.ir examples (ignore failures).
    fetch(`https://map.ir/vector/load?x-api-key=${encodeURIComponent(apiKey)}`, { method: 'POST' }).catch(() => {});

    // Optional RTL plugin for Persian/Arabic labels (ignore if blocked).
    try {
      mapboxgl.setRTLTextPlugin(
        'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js',
        undefined,
        true
      );
    } catch {
      // ignore
    }

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'https://map.ir/vector/styles/main/mapir-xyz-style.json',
      center: centerLngLat,
      zoom,
      transformRequest: (url) => {
        if (typeof url === 'string' && url.includes('map.ir/')) {
          return {
            url,
            headers: {
              'x-api-key': apiKey,
              'Mapir-SDK': 'reactjs',
            },
          };
        }
        return { url };
      },
    });

    mapRef.current = map;

    try {
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-left');
      map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');
    } catch {
      // ignore
    }

    const ensureRouteLayer = () => {
      if (!map.getSource('route')) {
        map.addSource('route', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: [] },
            properties: {},
          },
        });
      }
      if (!map.getLayer('route-line')) {
        map.addLayer({
          id: 'route-line',
          type: 'line',
          source: 'route',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-color': lineColor, 'line-width': lineWidth },
        });
      }
    };

    const flushPendingStyleIfAny = () => {
      const pending = pendingStyleRef.current;
      if (!pending) return;
      pendingStyleRef.current = null;
      try {
        map.setStyle(pending);
      } catch {
        // ignore
      }
    };

    const onLoad = () => {
      flushPendingStyleIfAny();
      ensureRouteLayer();
      setLoaded(true);
    };
    const onStyleLoad = () => {
      flushPendingStyleIfAny();
      ensureRouteLayer();
      setLoaded(true);
    };

    map.on('load', onLoad);
    map.on('style.load', onStyleLoad);

    return () => {
      markerRefs.current.forEach((m) => m.remove());
      markerRefs.current = [];
      pendingStyleRef.current = null;
      map.off('load', onLoad);
      map.off('style.load', onStyleLoad);
      map.remove();
      mapRef.current = null;
      setLoaded(false);
    };
  }, [apiKey, centerLngLat, zoom, lineColor, lineWidth]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;

    const coords = (lineLatLngs || [])
      .map((p) => {
        if (!Array.isArray(p) || p.length < 2) return null;
        const lat = Number(p[0]);
        const lng = Number(p[1]);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return [lng, lat];
      })
      .filter(Boolean);

    const src = map.getSource('route');
    if (src && typeof src.setData === 'function') {
      src.setData({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: {},
      });
    }

    try {
      if (map.getLayer('route-line')) {
        map.setPaintProperty('route-line', 'line-color', lineColor);
        map.setPaintProperty('route-line', 'line-width', lineWidth);
      }
    } catch {
      // ignore
    }

    markerRefs.current.forEach((m) => m.remove());
    markerRefs.current = [];

    (markers || []).forEach((m) => {
      const lat = Number(m.lat);
      const lng = Number(m.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      const el = document.createElement('div');
      el.style.width = '14px';
      el.style.height = '14px';
      el.style.borderRadius = '999px';
      el.style.background = '#ffffff';
      el.style.border = `3px solid ${lineColor}`;
      el.style.boxShadow = '0 6px 18px rgba(0,0,0,0.15)';

      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' }).setLngLat([lng, lat]);

      if (m.title || m.subtitle) {
        const safeTitle = String(m.title || '');
        const safeSubtitle = String(m.subtitle || '');
        marker.setPopup(
          new mapboxgl.Popup({ offset: 18 }).setHTML(
            `<div dir="rtl" style="text-align:right; font-family: inherit;">
              <div style="font-weight:800; font-size:12px; color:#111827;">${escapeHtml(safeTitle)}</div>
              ${safeSubtitle ? `<div style="margin-top:4px; font-size:11px; color:#4B5563;">${escapeHtml(safeSubtitle)}</div>` : ''}
            </div>`
          )
        );
      }

      marker.addTo(map);
      markerRefs.current.push(marker);
    });
  }, [loaded, lineLatLngs, markers, lineColor, lineWidth]);

  return <div ref={containerRef} className="absolute inset-0" />;
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
